import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, TableItem } from 'components/atoms';
import { FormInput, MagellanPopups } from 'components/molecules';
import LabeledStatementConsistancy from 'components/molecules/labeledStatus/LabeledStatementConsistancy';
import LabeledStatementStatus from 'components/molecules/labeledStatus/LabeledStatementStatus';
import { FooterTable, QueryFilter, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useComponentVisible } from 'hooks';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiNotepadBold } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { useGetMagellanAccountsLightList } from 'services/accounts';
import { useGetPredefindedSalesChannels } from 'services/enums';
import { useGetSettlementLightList } from 'services/settlementPeriods';
import {
  useGetStatementListInfinite,
  useGetStatementStatistics,
} from 'services/statements';
import { IStatement, STATEMENT_CONSISTANCY } from 'services/statements/types';
import { useTokenData } from 'store/state';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatCurrency } from 'utils/global/currency.utils';
import { getSettlementPeriodName } from 'utils/global/name.utils';
import { getNodes } from 'utils/table/nodes.utils';

enum STATEMENT_COLUMNS {
  settlementPeriod,
  salesChannel,
  provider,
  totalValue,
  consistency,
  hasUnrelatedRows,
  status,
}

interface IStatementsSearch {
  magellanAccountId: string;
  startSettlementPeriodId: string;
  endSettlementPeriodId: string;
  salesChannelId: string;
  showOtherStatuses: boolean;
  hasUnrelatedRows: boolean | '';
}

const initialFilters: IStatementsSearch = {
  magellanAccountId: '',
  startSettlementPeriodId: '',
  endSettlementPeriodId: '',
  salesChannelId: '',
  showOtherStatuses: false,
  hasUnrelatedRows: '',
};

const statementCurrencySuffix: Record<number, string> = {
  0: '',
  1: 'PLN',
  2: 'USD',
  3: 'EUR',
  4: 'CHF',
  5: 'GBP',
};

const Statements = () => {
  const tokenData = useTokenData();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const i18nextPrefix = 'statements';

  const [filters, setFilters] = useState<IStatementsSearch>(initialFilters);

  const columnsVisibility = useTableColumnsVisibility(
    'StatementsList1',
    STATEMENT_COLUMNS
  );

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);

  const accounts = useGetMagellanAccountsLightList();

  const canListSettlementPeriods = Boolean(
    tokenData?.['SettlementPeriodPermissionsLIST**' as keyof typeof tokenData]
  );

  const settlementPeriods = useGetSettlementLightList(
    filters.magellanAccountId,
    canListSettlementPeriods && Boolean(filters.magellanAccountId)
  );
  const salesChannels = useGetPredefindedSalesChannels(
    filters.magellanAccountId,
    Boolean(filters.magellanAccountId)
  );

  const accountsOptions = useMemo(
    () =>
      accounts.data?.map(account => ({
        value: account.id,
        label: account.accountName,
      })) ?? [],
    [accounts.data]
  );

  const settlementPeriodOptions = useMemo(
    () =>
      settlementPeriods.data?.map(s => ({
        label: getSettlementPeriodName(s.settlementPeriodName) ?? '…',
        value: s.id,
      })) ?? [],
    [settlementPeriods.data]
  );

  const salesChannelOptions = useMemo(
    () =>
      salesChannels.data?.map(c => ({
        value: String(c.id),
        label: c.name,
      })) ?? [],
    [salesChannels.data]
  );

  const listParams = useMemo(
    () => ({
      ...(filters.magellanAccountId
        ? { magellanAccountId: filters.magellanAccountId }
        : {}),
      page: 0,
      itemCount: 25,
      startSettlementPeriodId: filters.startSettlementPeriodId || undefined,
      endSettlementPeriodId: filters.endSettlementPeriodId || undefined,
      salesChannels: filters.salesChannelId
        ? [filters.salesChannelId]
        : undefined,
      hasUnrelatedRows:
        filters.hasUnrelatedRows === ''
          ? undefined
          : filters.hasUnrelatedRows,
      showOtherStatuses: filters.showOtherStatuses,
    }),
    [filters]
  );

  const { data, isLoading, fetchNextPage, isFetchingNextPage, refetch } =
    useGetStatementListInfinite(listParams);

  const statementStatistic = useGetStatementStatistics(listParams);

  const flatStatements = useMemo(
    () => data?.pages.flat() ?? [],
    [data?.pages]
  );

  const tableData = getNodes<IStatement>(flatStatements);

  const totalValueLength = formatCurrency(
    statementStatistic?.data?.allAmount
  ).length;

  const booleanSelectOptions = [
    { value: true, label: t('input.option.yes') },
    { value: false, label: t('input.option.no') },
  ];

  const select = useRowSelect(tableData, {
    onChange: (_action: Action, state: State) => {
      const id = state.id ?? state.ids?.[state.ids.length - 1];
      const item = flatStatements.find(row => row.id === id);
      const accountId =
        item?.magellanAccountId ?? filters.magellanAccountId;

      if (id && accountId) {
        navigate(`/statements/${accountId}/${id}/exchangeRate`);
      }
    },
  });

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title.list`)}
          path=''
          customBackItem={<div />}
          topbarContent={
            <>
              <QueryFilter.ButtonOpener
                i18nextPrefix={i18nextPrefix}
                onClick={handleComponentVisible}
                variant={BUTTON_VARIANT.default}
                isClear
              />
              <Table.CustomColumnsButton
                columns={STATEMENT_COLUMNS}
                isClear
                visibility={columnsVisibility}
                i18nextPrefix='input.label'
                customI18next=''
              />
              <MagellanPopups.StatementUpload
                defaultMagellanAccountId={filters.magellanAccountId}
                onSuccess={() => refetch()}
                trigger={
                  <Button variant={BUTTON_VARIANT.submit}>
                    <Button.Text text={t('button.add-statement')} />
                    <Button.Icon icon={<PiNotepadBold />} />
                  </Button>
                }
              />
            </>
          }>
          <QueryFilter<IStatementsSearch>
            i18nextPrefix={i18nextPrefix}
            initialValues={filters}
            onSubmit={() => {}}>
            <>
              <QueryFilter.Form<IStatementsSearch>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={values => setFilters(values)}>
                <QueryFilter.InputsGrid>
                  <FormInput.Select
                    name='magellanAccountId'
                    label={t('input.label.magellanAccount')}
                    options={accountsOptions}
                  />
                  <FormInput.Select
                    name='startSettlementPeriodId'
                    label={t('input.label.startSettlementPeriod')}
                    placeholder=''
                    options={settlementPeriodOptions}
                  />
                  <FormInput.Select
                    name='endSettlementPeriodId'
                    placeholder=''
                    label={t('input.label.endSettlementPeriod')}
                    options={settlementPeriodOptions}
                  />
                  <FormInput.Select
                    name='salesChannelId'
                    label={t('input.label.salesChannel')}
                    options={salesChannelOptions}
                    isDisabled={!filters.magellanAccountId}
                  />
                  <FormInput.Select
                    name='hasUnrelatedRows'
                    label={t('input.label.hasUnrelatedRows')}
                    options={booleanSelectOptions}
                  />
                  <FormInput.Switch
                    name='showOtherStatuses'
                    label={t('input.label.show-other-statuses')}
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilter.Labels
                isFormOpen={isComponentVisible}
                customLabel={data => {
                  if (data.key === 'magellanAccountId') {
                    return (
                      accountsOptions.find(o => o.value === data.value)
                        ?.label ?? '…'
                    );
                  }
                  if (data.key === 'startSettlementPeriodId') {
                    return (
                      settlementPeriodOptions.find(o => o.value === data.value)
                        ?.label ?? '…'
                    );
                  }
                  if (data.key === 'endSettlementPeriodId') {
                    return (
                      settlementPeriodOptions.find(o => o.value === data.value)
                        ?.label ?? '…'
                    );
                  }
                  if (data.key === 'salesChannelId') {
                    return (
                      salesChannelOptions.find(o => o.value === data.value)
                        ?.label ?? '…'
                    );
                  }
                  if (data.key === 'hasUnrelatedRows') {
                    return data.value !== ''
                      ? `${t('input.label.hasUnrelatedRows')}: ${
                          data.value === true
                            ? t('input.option.yes')
                            : t('input.option.no')
                        }`
                      : undefined;
                  }
                  if (data.key === 'showOtherStatuses') {
                    return data.value
                      ? t('input.label.show-other-statuses')
                      : undefined;
                  }
                  return `${data.value}`;
                }}
              />
            </>
          </QueryFilter>
        </TopBar>
      }>
      <FooterTable
        loading={isLoading || isFetchingNextPage}
        onFullyScroll={fetchNextPage}
        id={columnsVisibility.tableId}
        data={tableData}
        select={select}
        columns={[
            {
              label: t('input.label.settlementPeriod'),
              renderCell: (item: IStatement) => (
                <TableItem>
                  <TableItem.Text
                    text={getSettlementPeriodName(item.settlementPeriodName)}
                  />
                </TableItem>
              ),
              resize: true,
              hide: columnsVisibility.hide(STATEMENT_COLUMNS.settlementPeriod),
              footer: t('table.total'),
            },
            {
              label: t('input.label.salesChannel'),
              renderCell: (item: IStatement) => (
                <TableItem>
                  <TableItem.Text text={item.salesChannelName} />
                </TableItem>
              ),
              resize: true,
              hide: columnsVisibility.hide(STATEMENT_COLUMNS.salesChannel),
            },
            {
              label: t('input.label.provider'),
              renderCell: (item: IStatement) => (
                <TableItem>
                  <TableItem.Text text={item.providerName} />
                </TableItem>
              ),
              resize: true,
              hide: columnsVisibility.hide(STATEMENT_COLUMNS.provider),
            },
            {
              label: t('input.label.totalValue'),
              renderCell: (item: IStatement) => (
                <TableItem>
                  <TableItem.Currency
                    text={formatCurrency(item.defaultTotalValue, {
                      length: totalValueLength,
                      suffix:
                        statementCurrencySuffix[item.defaultCurrency] ?? '',
                    })}
                  />
                </TableItem>
              ),
              resize: true,
              hide: columnsVisibility.hide(STATEMENT_COLUMNS.totalValue),
              footer: formatCurrency(statementStatistic.data?.allAmount, {
                length: totalValueLength,
              }),
            },
            {
              label: t('input.label.status'),
              renderCell: (item: IStatement) => (
                <TableItem justifyPosition='center'>
                  <LabeledStatementStatus status={item.statementStatus} />
                </TableItem>
              ),
              resize: true,
              hide: columnsVisibility.hide(STATEMENT_COLUMNS.status),
            },
            {
              label: t('input.label.consistency'),
              renderCell: (item: IStatement) => (
                <TableItem justifyPosition='center'>
                  {item.consistency?.id != null && (
                    <LabeledStatementConsistancy
                      status={item.consistency.id as STATEMENT_CONSISTANCY}
                    />
                  )}
                </TableItem>
              ),
              resize: true,
              hide: columnsVisibility.hide(STATEMENT_COLUMNS.consistency),
            },
            {
              label: t('input.label.hasUnrelatedRows'),
              renderCell: (item: IStatement) => (
                <TableItem>
                  <TableItem.Text
                    text={
                      item.hasUnrelatedRows
                        ? t('input.option.yes')
                        : t('input.option.no')
                    }
                  />
                </TableItem>
              ),
              resize: true,
              hide: columnsVisibility.hide(STATEMENT_COLUMNS.hasUnrelatedRows),
            },
          ]}
        displayMode
      />
    </TopbarPage>
  );
};

export default Statements;
